import { useLocation } from 'react-router-dom';
import { ArrowRightIcon, SparklesIcon, ShoppingBagIcon, MessageCircleIcon } from 'lucide-react';
import { useCart, useOverlayActions } from '../../contexts/StoreContext';
import { useScrolledPast } from '../../hooks/useScrolledPast';
import { founder, salon } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { pageActions } from '../../data/pageActions';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { btnBar, btnBarQuiet, btnPriceBar } from '../../ui/styles';
import { FloatingBar, FloatingBarSpacer } from './FloatingBar';
const PRIMARY = `${btnBar} flex-[1.35]`;
const SECONDARY = `${btnBarQuiet} flex-1`;

/** Zichtbaar zodra je voorbij 60% van het eerste scherm bent: daarboven staan de knoppen van de hero zelf. */
const SHOW_AFTER = 0.6;
type StepProps = {
  pathname: string;
  openQuiz: () => void;
  openBooking: () => void;
};

/**
 * De volgende stap per pagina, zolang de mand leeg is. Altijd twee benoemde knoppen (geen raadsel-
 * icoontjes): salon = boeken + huidtest, contact = WhatsApp + boeken, de rest = huidtest + boeken.
 * De woorden komen uit `pageActions`, net als in de header en het menu.
 */
function NextStep({
  pathname,
  openQuiz,
  openBooking
}: StepProps) {
  const quiz = <>
      <SparklesIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7847" data-mp="33th2549usu95" />
      {pageActions.quiz}
    </>;
  if (pathname.startsWith('/behandelingen')) {
    return <>
        <button type="button" onClick={openBooking} aria-haspopup="dialog" className={PRIMARY} data-id="element-7848" data-mp="anu16zugxmd">
          {pageActions.book}
        </button>
        <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={SECONDARY} data-id="element-7849" data-mp="3hq2y29q5s90x">
          {quiz}
        </button>
      </>;
  }
  if (pathname.startsWith('/contact')) {
    return <>
        <a href={salon.whatsapp} target="_blank" rel="noopener noreferrer" className={PRIMARY} data-id="element-7850" data-mp="j26tx4yknvk6">
          <MessageCircleIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7851" data-mp="j4006j59wdrn" />
          App {founder.firstName}
          <span className="sr-only" data-id="element-7852" data-mp="nyt178tj0yta">{shopLabels.viaWhatsApp}</span>
        </a>
        <button type="button" onClick={openBooking} aria-haspopup="dialog" className={SECONDARY} data-id="element-7853" data-mp="izpuzl1t8mjq">
          {pageActions.book}
        </button>
      </>;
  }
  return <>
      <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={PRIMARY} data-id="element-7854" data-mp="f0qss87gr3vz">
        {quiz}
        <span className="text-shell/80" data-id="element-7855" data-mp="58l3554ooyjy">· 1 min</span>
      </button>
      <button type="button" onClick={openBooking} aria-haspopup="dialog" className={SECONDARY} data-id="element-7856" data-mp="5705h6n2ozhv">
        {pageActions.book}
      </button>
    </>;
}

/**
 * Zwevende actiebalk op telefoon en tablet. Met iets in de mand: de winkelmand, met de voortgang
 * naar het volgende cadeau als dunne lijn. Anders: de volgende stap van de pagina.
 * Verschijnt pas na het eerste scherm (behalve met iets in de mand), zodat hij de hero niet dubbelt.
 * Productpagina en Mijn omgeving hebben een eigen balk; afrekenen blijft afleidingsvrij.
 */
export function MobileActionBar() {
  const {
    openQuiz,
    openCart,
    openBooking
  } = useOverlayActions();
  const {
    count,
    subtotal,
    giftSubtotal,
    nextGift
  } = useCart();
  const {
    pathname
  } = useLocation();
  const scrolled = useScrolledPast(SHOW_AFTER);
  if (pathname === '/afrekenen' || pathname.startsWith('/bestelling/')) return null;
  if (pathname.startsWith('/webshop/') || pathname.startsWith('/account')) return <FloatingBarSpacer data-id="element-7857" data-mp="1j7wkpbuyw7yw" />;
  const giftLeft = nextGift ? Math.max(0, nextGift.threshold - giftSubtotal) : 0;
  const giftProgress = nextGift ? Math.min(1, giftSubtotal / nextGift.threshold) : 0;
  return <>
      <FloatingBarSpacer data-id="element-7858" data-mp="1sy94v2yvdz4i" />
      <FloatingBar show={count > 0 || scrolled} data-id="element-7859" data-mp="1syr6yg87r7kc">
        {count > 0 ? <>
            {nextGift && <div className="px-4 pb-2 pt-1.5" data-id="element-7860" data-mp="vibtk5onu4ej">
                <p className="text-[13px] text-ink" data-id="element-7861" data-mp="10h6m3zcfj7bu">
                  Nog <span className="tabular-nums" data-id="element-7862" data-mp="10flrk9q47odz">{euro(giftLeft)}</span> tot {nextGift.phrase}
                </p>
                <div aria-hidden="true" className="mt-1.5 h-0.5 w-full overflow-hidden rounded-full bg-line" data-id="element-7863" data-mp="2y590a012o79a">
                  <div className="h-full origin-left rounded-full bg-sage transition-transform duration-300 ease-soft" style={{
              transform: `scaleX(${giftProgress})`
            }} data-id="element-7864" data-mp="3bwf6sg2rr55o" />
                </div>
              </div>}
            <button type="button" onClick={openCart} aria-haspopup="dialog" className={btnPriceBar} data-id="element-7865" data-mp="3lu1w7whp2f3w">
              <span className="flex min-w-0 items-center gap-2" data-id="element-7866" data-mp="3gup1kpkl03qr">
                <ShoppingBagIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7867" data-mp="3uvjffnqeewwt" />
                <span className="truncate" data-id="element-7868" data-mp="3qu7j6zd0rdfn">
                  {plural(count, 'product', 'producten')} · <span className="tabular-nums" data-id="element-7869" data-mp="3qs3eif7aqaeb">{euro(subtotal)}</span>
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5" data-id="element-7870" data-mp="1wg6kcehgyqfk">
                Bekijk
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7871" data-mp="1mbyi5f7xsio0" />
              </span>
            </button>
          </> : <div className="flex gap-1" data-id="element-7872" data-mp="1qd21ibp70wtz">
            <NextStep pathname={pathname} openQuiz={openQuiz} openBooking={() => openBooking()} data-id="element-7873" data-mp="1cm6m0gk6vhhf" />
          </div>}
      </FloatingBar>
    </>;
}